/**
 * Sub-components + helpers for ``InlineClarificationBlock``.
 * Split out so the main component file stays under the
 * ``max-lines`` lint threshold; mirrors the
 * ``ask-user-details.choices.tsx`` split that the modal renderer
 * uses for the same reason.
 */

import { cn } from '@heidi/utils';
import { Pencil } from 'lucide-react';
import type { RefObject } from 'react';

interface OptionRowProps {
  badge: string;
  label: string;
  description?: string;
  isSelected: boolean;
  disabled?: boolean;
  onClick: () => void;
}

export const OptionRow = ({
  badge,
  label,
  description,
  isSelected,
  disabled = false,
  onClick,
}: OptionRowProps): JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={isSelected}
    className={cn(
      'group flex w-full gap-2 rounded-lg p-2 text-left transition-colors',
      // Single-line options center against the badge tile; rows with a
      // description are multi-line blocks and read better top-aligned.
      description ? 'items-start' : 'items-center',
      isSelected ? 'bg-surface-hover' : 'bg-surface hover:bg-surface-hover',
      disabled && 'cursor-not-allowed opacity-60'
    )}
  >
    <span
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-md text-xs font-normal',
        isSelected
          ? 'bg-background-primary text-text-primary-invert group-hover:bg-background-primary-hover'
          : 'bg-background-secondary text-text-secondary group-hover:bg-background-secondary-hover'
      )}
    >
      {badge}
    </span>
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span
        className={cn(
          'break-words text-sm font-normal leading-normal',
          isSelected ? 'text-text-primary' : 'text-text-secondary'
        )}
      >
        {label}
      </span>
      {description && (
        <span className="break-words text-xs leading-snug text-text-secondary">
          {description}
        </span>
      )}
    </span>
  </button>
);
interface OtherRowProps {
  inputRef: RefObject<HTMLInputElement>;
  value: string;
  placeholder: string;
  isSelected: boolean;
  disabled?: boolean;
  onChange: (next: string) => void;
  onFocus: () => void;
  onRowClick: () => void;
  /** Enter (without Shift) in the free-text input → submit the answer
   *  ("Continue"), so the user doesn't have to reach for the button. */
  onSubmit?: () => void;
}

export const OtherRow = ({
  inputRef,
  value,
  placeholder,
  isSelected,
  disabled = false,
  onChange,
  onFocus,
  onRowClick,
  onSubmit,
}: OtherRowProps): JSX.Element => (
  <div
    onClick={() => {
      if (disabled) return;
      onRowClick();
    }}
    // Marks the row as interactive for the overlay drag filter so that
    // mousedown on the badge / row padding doesn't get swallowed by the
    // panel-drag handler when this card renders inside an overlay
    // approval surface (see useOverlayWindowDrag).
    data-overlay-interactive="true"
    className={cn(
      'group flex items-center gap-2 rounded-lg p-2 transition-colors',
      isSelected ? 'bg-surface-hover' : 'bg-surface hover:bg-surface-hover',
      disabled && 'cursor-not-allowed opacity-60'
    )}
    data-testid="clarification-other-row"
  >
    <span
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-md',
        isSelected
          ? 'bg-background-primary text-text-primary-invert group-hover:bg-background-primary-hover'
          : 'bg-background-secondary text-text-secondary group-hover:bg-background-secondary-hover'
      )}
    >
      <Pencil className="size-3.5" />
    </span>
    <input
      ref={inputRef}
      type="text"
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onFocus={onFocus}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          onSubmit?.();
        }
      }}
      className={cn(
        'min-w-0 flex-1 bg-transparent text-sm text-text-primary',
        'placeholder:text-text-tertiary',
        'focus:outline-none',
        'disabled:cursor-not-allowed'
      )}
    />
  </div>
);
